import { X } from "lucide-react";
import type { ReactNode } from "react";
import { Button } from "@/components/ui/Button";
import { S, T } from "@/lib/strings";
import { cn } from "@/lib/utils";
import type { Selection } from "@/types/fleet";

/**
 * The sticky bar that appears once anything is selected.
 *
 * Two selection modes, and the difference matters: 「已選 50 部（本頁）」 is a
 * list of ids the operator can see, while 「選取全部符合條件的 87 部」 stores a
 * **filter descriptor** — the server resolves it at action time. Materialising
 * 87 ids in the browser would mean acting on a snapshot that was already stale
 * when it was taken, and it caps the fleet at whatever fits in one page.
 */
export function BulkActionBar({
  selection,
  pageCount,
  matchingCount,
  onSelectAllMatching,
  onClear,
  children,
}: {
  selection: Selection | null;
  /** How many rows on the current page are ticked. */
  pageCount: number;
  /**
   * How many rows match the active filter, if known. There is no total-count
   * endpoint, so this is an estimate from the dispatch preview or the loaded
   * pages — never presented as authoritative.
   */
  matchingCount: number | null;
  onSelectAllMatching: () => void;
  onClear: () => void;
  children: ReactNode;
}) {
  if (!selection) return null;

  const isFilterMode = selection.kind === "filter";
  const selectedCount = isFilterMode ? selection.estimated : selection.ids.length;
  if (selectedCount === 0) return null;

  // Offer "select everything matching" whenever there is plausibly more than
  // this page. When the count is unknown — more pages are still unloaded and
  // there is no count endpoint — say so rather than inventing a number.
  const canOfferAll =
    !isFilterMode && pageCount > 0 && (matchingCount === null || matchingCount > pageCount);

  return (
    <div
      className={cn(
        "sticky bottom-0 z-20 -mx-6 mt-3 border-t border-[var(--color-border)] px-6 py-2.5 md:-mx-8 md:px-8",
        "bg-[var(--color-panel)]/95 backdrop-blur supports-[backdrop-filter]:bg-[var(--color-panel)]/80",
      )}
      role="region"
      aria-label="批次操作"
    >
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
        <span className="text-[13px] font-medium tracking-[-0.165px] text-[var(--color-fg)]">
          {isFilterMode ? T.selectedAllMatching(selectedCount) : T.selectedOnPage(selectedCount)}
        </span>

        {canOfferAll && (
          <>
            <span className="text-[var(--color-fg-faint)]">·</span>
            <button
              type="button"
              onClick={onSelectAllMatching}
              className="text-[13px] font-medium text-[var(--color-accent)] underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-[var(--color-accent)]"
            >
              {matchingCount === null
                ? S.bulkSelectAllMatchingUnknown
                : T.selectAllMatching(matchingCount)}
            </button>
          </>
        )}

        <div className="ml-auto flex items-center gap-2">
          {children}
          <Button variant="ghost" size="sm" onClick={onClear} aria-label={S.bulkClear}>
            <X size={13} />
            {S.bulkClear}
          </Button>
        </div>
      </div>

      {isFilterMode && (
        <p className="mt-1 text-[11px] text-[var(--color-fg-faint)]">
          以篩選條件選取：實際受影響的裝置由伺服器在執行時決定，數字可能與現在略有出入。
        </p>
      )}
    </div>
  );
}
